import React, { useRef, useState, useCallback } from "react";
import { NodeViewWrapper } from "@tiptap/react";

const ALIGN_CLASS = {
  left: "float-left mr-5 mb-2 rounded-md",
  right: "float-right ml-5 mb-2 rounded-md",
  center: "block mx-auto my-2 rounded-md clear-both",
};

const HANDLE_BASE =
  "absolute h-3 w-3 rounded-sm bg-white border-2 border-[#0A0F17] shadow ring-1 ring-black/10 z-20";

export default function ResizableDailymotionNodeView({
  node,
  selected,
  updateAttributes,
  editor,
}) {
  const wrapperRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [livePercent, setLivePercent] = useState(null);

  const alignment = node.attrs.alignment || "center";
  const widthAttr = node.attrs.width || 100;
  const containerClass = ALIGN_CLASS[alignment] || ALIGN_CLASS.center;

  const visiblePercent = livePercent ?? widthAttr;

  const startDrag = useCallback(
    (corner) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      const editorEl =
        editor?.view?.dom || wrapperRef.current?.closest(".tiptap-prose");
      const editorRect = editorEl?.getBoundingClientRect();
      const editorWidth = editorRect?.width || 600;
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const startX = e.clientX;
      const startWidth = wrapperRect?.width || editorWidth;
      const dir = corner === "tr" || corner === "br" ? 1 : -1;

      setDragging(true);

      // Disable pointer events on all iframes so they do not steal mouse events during dragging
      const iframes = document.querySelectorAll("iframe");
      iframes.forEach((ifr) => ifr.style.pointerEvents = "none");

      const onMove = (ev) => {
        const delta = (ev.clientX - startX) * dir;
        const newPx = Math.max(100, startWidth + delta); // min 100px so it stays grabbable
        const pct = Math.min(
          100,
          Math.max(10, Math.round((newPx / editorWidth) * 100)),
        );
        setLivePercent(pct);
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        // Re-enable pointer events on iframes
        const ifrs = document.querySelectorAll("iframe");
        ifrs.forEach((ifr) => ifr.style.pointerEvents = "");

        setDragging(false);
        setLivePercent((finalPct) => {
          if (finalPct != null) updateAttributes({ width: finalPct });
          return null;
        });
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [editor, updateAttributes],
  );

  const inlineStyle = {
    width: `${visiblePercent}%`,
    aspectRatio: "16 / 9",
    position: "relative",
  };

  // Convert a dai.ly / watch / hub URL to the canonical embed src if needed.
  let embedSrc = node.attrs.src;
  if (embedSrc && !embedSrc.includes("/embed/")) {
    const dmMatch = embedSrc.match(/(?:dailymotion\.com\/(?:video|hub)\/|dai\.ly\/)([a-zA-Z0-9]+)/i);
    if (dmMatch) {
      embedSrc = `https://www.dailymotion.com/embed/video/${dmMatch[1]}`;
    }
  }

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      as="div"
      data-alignment={alignment}
      data-width={widthAttr}
      className={`relative ${containerClass} ${
        selected ? "outline outline-2 outline-[#6CABDD]" : ""
      }`}
      style={inlineStyle}
      contentEditable={false}
      draggable={false}
    >
      <iframe
        src={embedSrc}
        title="Dailymotion video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        className="block h-full w-full rounded-md pointer-events-none"
        draggable={false}
      />

      {/* Live size badge while dragging */}
      {dragging && livePercent != null ? (
        <span
          className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded-sm bg-[#0A0F17] px-2 py-0.5 font-display text-xs font-bold text-white z-30"
          data-testid="dailymotion-resize-badge"
        >
          {livePercent}%
        </span>
      ) : null}

      {/* Resize handles — only when selected */}
      {selected ? (
        <>
          <span
            data-testid="dailymotion-handle-tl"
            onMouseDown={startDrag("tl")}
            className={`${HANDLE_BASE} -left-1.5 -top-1.5 cursor-nwse-resize`}
          />
          <span
            data-testid="dailymotion-handle-tr"
            onMouseDown={startDrag("tr")}
            className={`${HANDLE_BASE} -right-1.5 -top-1.5 cursor-nesw-resize`}
          />
          <span
            data-testid="dailymotion-handle-bl"
            onMouseDown={startDrag("bl")}
            className={`${HANDLE_BASE} -bottom-1.5 -left-1.5 cursor-nesw-resize`}
          />
          <span
            data-testid="dailymotion-handle-br"
            onMouseDown={startDrag("br")}
            className={`${HANDLE_BASE} -bottom-1.5 -right-1.5 cursor-nwse-resize`}
          />
        </>
      ) : null}
    </NodeViewWrapper>
  );
}
